import { useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_term', 'utm_content'] as const;
const UTM_STORAGE_KEY = 'kitmarca_utm';
const SESSION_ID_KEY = 'kitmarca_session';

export interface UtmParams {
  utm_source?: string;
  utm_medium?: string;
  utm_campaign?: string;
  utm_term?: string;
  utm_content?: string;
}

// Generate unique session ID
function generateSessionId(): string {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

// Get or create session ID
export function getSessionId(): string {
  let sessionId = sessionStorage.getItem(SESSION_ID_KEY);
  if (!sessionId) {
    sessionId = generateSessionId();
    sessionStorage.setItem(SESSION_ID_KEY, sessionId);
  }
  return sessionId;
}

// Capture UTMs from URL and store in localStorage
export function captureUtmParams(): UtmParams {
  const urlParams = new URLSearchParams(window.location.search);
  const utmParams: UtmParams = {};
  
  UTM_KEYS.forEach((key) => {
    const value = urlParams.get(key);
    if (value) {
      utmParams[key] = value;
    }
  });
  
  // Only save if we found UTM params
  if (Object.keys(utmParams).length > 0) {
    localStorage.setItem(UTM_STORAGE_KEY, JSON.stringify(utmParams));
  }
  
  return utmParams;
}

// Get stored UTMs from localStorage
export function getStoredUtmParams(): UtmParams {
  try {
    const stored = localStorage.getItem(UTM_STORAGE_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch {
    return {};
  }
}

// Track analytics event
export async function trackEvent(
  eventType: string,
  eventData: Record<string, unknown> = {}
): Promise<void> {
  const utmParams = getStoredUtmParams();
  const sessionId = getSessionId();
  
  try {
    // Check if user is authenticated before tracking
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) {
      // Silently skip tracking for unauthenticated users to avoid 401 errors
      return;
    }
    
    await supabase.from('analytics_events').insert([{
      session_id: sessionId,
      event_type: eventType,
      event_data: eventData as unknown as Record<string, never>,
      utm_source: utmParams.utm_source,
      utm_medium: utmParams.utm_medium,
      utm_campaign: utmParams.utm_campaign,
      utm_term: utmParams.utm_term,
      utm_content: utmParams.utm_content,
      page_url: window.location.href,
      referrer: document.referrer || null,
      user_agent: navigator.userAgent,
    }]);
  } catch {
    // Silently ignore tracking errors to prevent console pollution
  }
}

// Update user profile with UTM data
export async function updateUserUtmData(userId: string): Promise<void> {
  const utmParams = getStoredUtmParams();
  
  if (Object.keys(utmParams).length === 0) return;
  
  try {
    await supabase
      .from('profiles')
      .update({
        utm_source: utmParams.utm_source,
        utm_medium: utmParams.utm_medium,
        utm_campaign: utmParams.utm_campaign,
        utm_term: utmParams.utm_term,
        utm_content: utmParams.utm_content,
        last_activity_at: new Date().toISOString(),
      })
      .eq('id', userId);
  } catch (error) {
    console.error('Error updating user UTM data:', error);
  }
}

// Hook to capture UTMs on page load
export function useUtmCapture() {
  useEffect(() => {
    captureUtmParams();
    trackEvent('page_view');
  }, []);
}

// Hook to track user activity and UTMs on auth
export function useUtmOnAuth() {
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event, session) => {
        if (event === 'SIGNED_IN' && session?.user) {
          // Use setTimeout to avoid Supabase deadlock
          setTimeout(() => {
            updateUserUtmData(session.user.id);
            trackEvent('user_signed_in', { user_id: session.user.id });
          }, 0);
        }
      }
    );
    
    return () => subscription.unsubscribe();
  }, []);
}
